// chillandout PDP polish — Phase 2 Bundle A.
//
// Two responsibilities, both for product pages only:
//   1. Magnetic CTA on Horizon's add-to-cart button
//      (matches the hero/cart-drawer signature without a JS web component)
//   2. Variant out-of-stock voice
//      ("that color's resting. try another?" §10.4 microcopy)
//
// Loaded conditionally on product templates via snippets/scripts.liquid.
// Defer-loaded; no module imports.
//
// Spec: docs/MASTER-BRIEF.md §10.4 + §7.4 (magnetic spring)
// Budget: ~2 KB gzip. No deps.

(function () {
  'use strict';

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    setupMagneticAddToCart();
    setupVariantVoice();
  }

  // ---------- Magnetic add-to-cart (§7.4) ----------
  // Hooke spring: stiffness 200, damping 25, mass 1, dt = 1/60.

  const STIFFNESS = 200;
  const DAMPING = 25;
  const DT = 1 / 60;
  const REST = 0.05;
  const RADIUS = 100;       // pull radius — bigger than hero CTA since the
                            // PDP button area is denser; gentler attraction
  const MAX_OFFSET = 6;     // gentler than hero (8) — secondary action context

  function setupMagneticAddToCart() {
    if (!matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const button = document.querySelector(
      '.add-to-cart-button, [data-add-to-cart-button], button.product-form__submit'
    );
    if (!button) return;

    button.classList.add('chill-magnetic');

    const state = { x: 0, y: 0, vx: 0, vy: 0, tx: 0, ty: 0, raf: 0, springRaf: 0, lastE: null };

    const move = (e) => {
      state.lastE = e;
      if (state.raf) return;
      state.raf = requestAnimationFrame(() => {
        state.raf = 0;
        const ev = state.lastE;
        if (!ev) return;
        const rect = button.getBoundingClientRect();
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        const dx = ev.clientX - cx;
        const dy = ev.clientY - cy;
        const dist = Math.hypot(dx, dy);
        if (dist < RADIUS) {
          const pull = (RADIUS - dist) / RADIUS;
          state.tx = (dx / RADIUS) * MAX_OFFSET * pull;
          state.ty = (dy / RADIUS) * MAX_OFFSET * pull;
        } else {
          state.tx = 0; state.ty = 0;
        }
        spring();
      });
    };

    const leave = () => {
      state.tx = 0; state.ty = 0;
      spring();
    };

    function spring() {
      if (state.springRaf) return;
      const tick = () => {
        const ax = (state.tx - state.x) * STIFFNESS - state.vx * DAMPING;
        const ay = (state.ty - state.y) * STIFFNESS - state.vy * DAMPING;
        state.vx += ax * DT;
        state.vy += ay * DT;
        state.x += state.vx * DT;
        state.y += state.vy * DT;
        button.style.setProperty('--magnetic-x', state.x.toFixed(2) + 'px');
        button.style.setProperty('--magnetic-y', state.y.toFixed(2) + 'px');
        const settled =
          Math.abs(state.tx - state.x) < REST &&
          Math.abs(state.vx) < REST &&
          Math.abs(state.ty - state.y) < REST &&
          Math.abs(state.vy) < REST;
        if (settled) {
          state.springRaf = 0;
          state.x = state.tx; state.y = state.ty;
          button.style.setProperty('--magnetic-x', state.x.toFixed(2) + 'px');
          button.style.setProperty('--magnetic-y', state.y.toFixed(2) + 'px');
          return;
        }
        state.springRaf = requestAnimationFrame(tick);
      };
      state.springRaf = requestAnimationFrame(tick);
    }

    // Listen on the parent so the radius can attract from outside the button bounds.
    const host = button.closest('.product-form, [data-product-form]') || button.parentElement;
    if (host) {
      host.addEventListener('mousemove', move);
      host.addEventListener('mouseleave', leave);
    }
  }

  // ---------- Variant voice (§10.4) ----------
  // When Horizon dispatches a VariantUpdateEvent and the new variant is
  // unavailable, swap the button text to chill voice. Restore on available.

  function setupVariantVoice() {
    const button = document.querySelector(
      '.add-to-cart-button, [data-add-to-cart-button]'
    );
    if (!button) return;

    const textNode = button.querySelector('.add-to-cart-text__content > span > span > span')
                  || button.querySelector('.add-to-cart-text__content span:last-of-type')
                  || button.querySelector('.add-to-cart-text__content');
    if (!textNode) return;

    // Cache the original text so we can restore on available.
    const originalText = textNode.textContent.trim();

    // Read locale key shipped via window.Theme.translations (Horizon convention)
    // or fall back to the canonical chill string.
    const unavailableText = (window.Theme && window.Theme.translations
      && window.Theme.translations.products_product_variant_unavailable)
      || "that color's resting. try another?";

    document.addEventListener('variant:update', handleVariantUpdate);
    document.addEventListener('VariantUpdate', handleVariantUpdate);

    function handleVariantUpdate(e) {
      const detail = e && e.detail;
      if (!detail || !detail.resource) return;
      const available = detail.resource.available;
      if (available === false) {
        textNode.textContent = unavailableText;
        button.classList.add('chill-variant-unavailable');
      } else if (available === true) {
        textNode.textContent = originalText;
        button.classList.remove('chill-variant-unavailable');
      }
    }
  }
})();
